import { useRef, useCallback } from "react";
import { View, StyleSheet, StyleProp, ViewStyle, Text, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { Stepper } from "@/components/ui/Stepper";
import { Button } from "@/components/ui/Button";
import useAuth from "@/hooks/useAuth";
import { useCalendarParams } from "@/lib/calendar";
import useDateTime from "@/hooks/useDateTime";
import useContextMenu from "@/hooks/useContextMenu";
import { DateTimePicker } from "@/components/Pickers/DatetimePicker";
import { useToolTip } from "@/hooks/useToolTip";

type MonthBannerProps = {
  style?: StyleProp<ViewStyle>;
  /** Shifts before today in the current view */
  pastShifts?: number;
  /** Issues in past shifts */
  pastIssues?: number;
  /** Shifts from today onwards in the current view */
  futureShifts?: number;
  /** Issues in future shifts */
  futureIssues?: number;
};

export function MonthBanner({ style, pastShifts, pastIssues, futureShifts, futureIssues }: MonthBannerProps) {
  const { colors } = useTheme();
  const { date, stepDay, stepWeek, goToToday, setDate } = useCalendarParams();
  const { isToday } = useDateTime();
  const contextMenu = useContextMenu();
  const toolTip = useToolTip();
  const pickerRef = useRef<View>(null);
  const todayRef = useRef<View>(null);
  const goToRef = useRef<View>(null);
  const isHovering = useRef(false);
  const { displayNames } = useAuth();

  // Check if current view includes today
  const viewIncludesToday = isToday(date);

  const showTooltip = useCallback(
    (text: string, ref: React.RefObject<View | null>) => {
      isHovering.current = true;
      setTimeout(() => {
        if (!isHovering.current) return;
        toolTip.setToolTipContent(
          <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
            <ThemedText style={{ fontSize: 12 }}>{text}</ThemedText>
          </View>,
        );
        ref.current?.measureInWindow((x, y) => {
          if (!isHovering.current) return;
          toolTip.setLocation({ x, y: y - 36 } as any);
          toolTip.setVisible(true);
        });
      }, 300);
    },
    [colors.background, colors.border, toolTip],
  );

  const hideTooltip = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  const openDatePicker = () => {
    contextMenu.setIgnoreRef(pickerRef);
    pickerRef.current?.measureInWindow((pageX, pageY, width, height) => {
      contextMenu.setLocation({ x: pageX, y: pageY + height + 8 });
    });
    contextMenu.setMenuContent(
      <DateTimePicker
        date={date}
        hideTime={true}
        fireOnDayPick={true}
        onPick={({ date: newDate }) => {
          setDate(newDate);
          contextMenu.setVisible(false);
        }}
      />,
    );
    contextMenu.setStyle({ backgroundColor: colors.background });
    contextMenu.setVisible(true);
  };

  return (
    <Stepper
      onJumpLeft={() => stepWeek("left")}
      onStepLeft={() => stepDay("left")}
      onJumpRight={() => stepWeek("right")}
      onStepRight={() => stepDay("right")}
      jumpLeftTooltip="Previous week"
      stepLeftTooltip="Previous day"
      stepRightTooltip="Next day"
      jumpRightTooltip="Next week"
      center={
        <View style={[styles.centerContainer, style]}>
          {/* Past stats - left side */}
          {pastShifts !== undefined && (
            <View style={styles.statWrapper}>
              {pastIssues !== undefined && pastIssues > 0 && (
                <View style={[styles.issueBadge, { backgroundColor: colors.dangerBorder }]}>
                  <Text style={styles.issueBadgeText}>{pastIssues}</Text>
                </View>
              )}
              <View style={[styles.stat, { backgroundColor: colors.primary + "20" }]}>
                <ThemedText style={[styles.statCount, { color: colors.primary }]}>{pastShifts}</ThemedText>
                <ThemedText style={[styles.statLabel, { color: colors.primary }]}>
                  {displayNames?.shifts?.plural}
                </ThemedText>
              </View>
            </View>
          )}

          {/* Left spacer */}
          <View style={styles.spacer} />

          {/* Navigation buttons - centered */}
          <View style={styles.navButtons}>
            <Pressable
              ref={todayRef}
              onHoverIn={() => showTooltip("Jump to today's date", todayRef)}
              onHoverOut={hideTooltip}
            >
              <Button
                text="Today"
                icon="calendar-today"
                style={[styles.navButton, { borderColor: colors.border }, viewIncludesToday && { opacity: 0.5 }]}
                textStyle={{ fontSize: 12 }}
                iconSize={14}
                onPress={goToToday}
                disabled={viewIncludesToday}
              />
            </Pressable>
            <Pressable
              ref={goToRef}
              onHoverIn={() => showTooltip("Pick a specific date", goToRef)}
              onHoverOut={hideTooltip}
            >
              <View ref={pickerRef}>
                <Button
                  text="Go to"
                  icon="calendar-search"
                  style={[styles.navButton, { borderColor: colors.border }]}
                  textStyle={{ fontSize: 12 }}
                  iconSize={14}
                  onPress={openDatePicker}
                />
              </View>
            </Pressable>
          </View>

          {/* Right spacer */}
          <View style={styles.spacer} />

          {/* Future stats - right side */}
          {futureShifts !== undefined && (
            <View style={styles.statWrapper}>
              {futureIssues !== undefined && futureIssues > 0 && (
                <View style={[styles.issueBadge, { backgroundColor: colors.dangerBorder }]}>
                  <Text style={styles.issueBadgeText}>{futureIssues}</Text>
                </View>
              )}
              <View style={[styles.stat, { backgroundColor: colors.primary + "20" }]}>
                <ThemedText style={[styles.statCount, { color: colors.primary }]}>{futureShifts}</ThemedText>
                <ThemedText style={[styles.statLabel, { color: colors.primary }]}>
                  {displayNames?.shifts?.plural}
                </ThemedText>
              </View>
            </View>
          )}
        </View>
      }
    />
  );
}

const styles = StyleSheet.create({
  centerContainer: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    width: "100%",
  },
  spacer: {
    flex: 1,
  },
  statWrapper: {
    position: "relative",
  },
  stat: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
  },
  statCount: {
    fontSize: 13,
    fontWeight: "600",
  },
  statLabel: {
    fontSize: 12,
  },
  issueBadge: {
    position: "absolute",
    top: -6,
    right: -6,
    minWidth: 18,
    height: 18,
    paddingHorizontal: 4,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
  },
  issueBadgeText: {
    color: "white",
    fontSize: 12,
    fontWeight: "600",
    textAlign: "center",
    lineHeight: 18,
  },
  navButtons: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  navButton: {
    height: 28,
    paddingHorizontal: 8,
    backgroundColor: "transparent",
    borderWidth: StyleSheet.hairlineWidth,
    gap: 4,
  },
  tooltip: {
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
});
